import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Play, Pause, RotateCcw } from "lucide-react";

const MODES = {
  pomodoro: { label: "Pomodoro", work: 25 * 60, brk: 5 * 60 },
  study: { label: "Study Session", work: 50 * 60, brk: 10 * 60 },
};

function fmt(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function TimerPage() {
  const [mode, setMode] = useState("pomodoro");
  const [phase, setPhase] = useState("work");
  const [remain, setRemain] = useState(MODES.pomodoro.work);
  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState([]);
  const [label, setLabel] = useState("");
  const startTsRef = useRef(null);

  useEffect(() => {
    api.get("/timer/logs").then((r) => setLogs(r.data));
  }, []);

  useEffect(() => {
    if (!running) return;
    const iv = setInterval(() => {
      setRemain((r) => {
        if (r <= 1) {
          onComplete();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [running]);

  const reset = () => {
    setRunning(false);
    setPhase("work");
    setRemain(MODES[mode].work);
  };

  useEffect(() => {
    reset();
    // eslint-disable-next-line
  }, [mode]);

  const start = () => {
    if (!running) {
      startTsRef.current = Date.now();
      setRunning(true);
    }
  };

  const pause = () => setRunning(false);

  const onComplete = async () => {
    setRunning(false);
    if (phase === "work") {
      const dur = MODES[mode].work / 60;
      try {
        await api.post("/timer/log", { kind: mode, duration_min: dur, label });
        toast.success(`⚡ ${dur} min ${MODES[mode].label} complete`);
        api.get("/timer/logs").then((r) => setLogs(r.data));
      } catch {}
      setPhase("break");
      setRemain(MODES[mode].brk);
    } else {
      toast.info("Break over. Back to the grind.");
      setPhase("work");
      setRemain(MODES[mode].work);
    }
  };

  const total = MODES[mode][phase === "work" ? "work" : "brk"];
  const pct = ((total - remain) / total) * 100;
  const circle = 2 * Math.PI * 90;

  return (
    <div className="space-y-6" data-testid="timer-page">
      <div>
        <div className="label-tech">Focus Chamber</div>
        <h1 className="h-display text-3xl sm:text-4xl mt-1">
          Deep <span className="text-cyan-400">Work</span>
        </h1>
      </div>

      <div className="flex justify-center gap-2">
        {Object.entries(MODES).map(([k, v]) => (
          <button
            key={k}
            data-testid={`timer-mode-${k}`}
            onClick={() => setMode(k)}
            className={`px-4 py-2 font-display uppercase text-xs tracking-widest border ${
              mode === k ? "bg-cyan-400 text-black border-cyan-400" : "border-white/10 text-slate-400 hover:text-white"
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      <div className="panel panel-corners p-6 flex flex-col items-center gap-4">
        <div className="label-tech" data-testid="timer-phase">{phase === "work" ? "Deep Focus" : "Recovery"}</div>
        <div className="relative w-56 h-56">
          <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90">
            <circle cx="100" cy="100" r="90" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
            <circle
              cx="100"
              cy="100"
              r="90"
              fill="none"
              stroke={phase === "work" ? "#00E5FF" : "#8B5CF6"}
              strokeWidth="8"
              strokeDasharray={circle}
              strokeDashoffset={circle - (pct / 100) * circle}
              strokeLinecap="round"
              style={{ transition: "stroke-dashoffset 1s linear", filter: "drop-shadow(0 0 8px currentColor)" }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-5xl font-mono-tech" data-testid="timer-display">
              {fmt(remain)}
            </div>
          </div>
        </div>

        <input
          data-testid="timer-label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Session label (e.g. Math)"
          className="bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none px-3 py-2 text-white text-sm w-64"
        />

        <div className="flex gap-2">
          {!running ? (
            <button data-testid="timer-start" onClick={start} className="btn-neon">
              <Play size={16} className="mr-1 inline" /> Start
            </button>
          ) : (
            <button data-testid="timer-pause" onClick={pause} className="btn-purple">
              <Pause size={16} className="mr-1 inline" /> Pause
            </button>
          )}
          <button data-testid="timer-reset" onClick={reset} className="btn-purple">
            <RotateCcw size={14} className="mr-1 inline" /> Reset
          </button>
        </div>
      </div>

      <div className="panel panel-corners p-6">
        <div className="label-tech mb-3">Recent Sessions</div>
        {logs.length === 0 ? (
          <p className="text-sm text-slate-500">No sessions yet. Ignite one.</p>
        ) : (
          <div className="space-y-1">
            {logs.map((l) => (
              <div key={l.id} className="flex justify-between text-sm border-b border-white/5 py-1.5">
                <span>
                  <span className="text-cyan-400 uppercase text-xs mr-2">{l.kind}</span>
                  {l.label || "—"}
                </span>
                <span className="font-mono-tech text-xs text-slate-400">
                  {l.duration_min}m · {l.day}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
